<template>
  <ControlWrapper
    v-bind="controlWrapper"
    :styles="styles"
    :is-focused="isFocused"
    :applied-options="appliedOptions"
    :error="control.errors"
  >
    <section class="flex w-full flex-col items-start">
      <slot name="header">
        <div class="flex w-full items-center justify-between" :class="styles.control.label">
          <h3>{{ computedLabel }}</h3>
          <div v-if="control?.data?.latitude" class="flex items-center gap-2">
            <WButton type="ghost" @click="toggleMap(true)">
              <WidgetIcon :icon="controlOptions.filled.edit" height="1.5rem" class="widget-action-icon" />
            </WButton>
            <WButton type="ghost" @click="isOpenDeleteModal = true">
              <WidgetIcon :icon="controlOptions.filled.delete" height="1.5rem" class="widget-action-icon-negative" />
            </WButton>
          </div>
        </div>
      </slot>
      <div class="flex w-full flex-row flex-nowrap justify-start gap-5">
        <GoogleMap
          v-if="typeof controlOptions === 'object'"
          :model-value="currentLocation"
          :is-open="isMapOpen"
          :main-icon="controlOptions.blank.icon"
          :btn-icon="controlOptions.blank.btnIcon"
          :confirm-text="controlOptions.overlay.confirm"
          :w3w-label="controlOptions.filled.w3wLabel"
          :location-label="controlOptions.filled.locationLabel"
          :cancel-text="controlOptions.overlay.cancel"
          :cancel-icon="themeIcons.close"
          :title="controlOptions.blank.cta"
          :search-icon="controlOptions.overlay.preIcon"
          :marker-icon="controlOptions.overlay.marker"
          @update:model-value="locationChange"
          @update:is-open="toggleMap"
        ></GoogleMap>
      </div>
      <Modal v-if="isOpenDeleteModal" v-model="isOpenDeleteModal" hide-close>
        <template #header><p class="title2-bold">Delete collision location?</p> </template>
        <div class="flex h-full w-full flex-col items-start gap-4">
          <p>Are you sure you want to delete this location?</p>
          <p>This action can not be undone</p>
        </div>
        <template #footer>
          <div class="w-full">
            <WButton type="secondary" class="widget-confirm-modal_btn_close" @click="isOpenDeleteModal = false">
              No, keep location
            </WButton>
            <WButton type="primary" class="widget-confirm-modal_btn" @click="deleteLocation"> Yes, I am sure </WButton>
          </div>
        </template>
      </Modal>
    </section>
  </ControlWrapper>
</template>

<script lang="ts">
  import { ControlElement } from '@jsonforms/core';
  import { rendererProps, RendererProps, useJsonFormsControl } from '@jsonforms/vue';
  import { defineComponent, ref } from 'vue';
  import { useWidgetControl } from '../util/composition';
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';
  import { config } from 'src/utils/config';
  import GoogleMap from 'components/base/GoogleMap.vue';
  import Button from 'components/base/Button.vue';
  import ControlWrapper from './ControlWrapper.vue';
  import Modal from 'src/components/base/ConfirmModal.vue';
  import { ILocationInfo } from '../../../utils/map';
  import { themeIcons } from 'src/utils/config';

  export type LocationOptions = {
    blank: Blank;
    overlay: Overlay;
    filled: Filled;
  };

  interface Filled {
    edit: string;
    delete: string;
    locationLabel?: string;
    w3wLabel?: string;
  }

  interface Overlay {
    confirm: string;
    cancel: string;
    preIcon: string;
    clearIcon: string;
    marker: string;
  }

  interface Blank {
    icon: string;
    cta: string;
    btnIcon?: string;
  }

  export default defineComponent({
    name: 'Location',
    components: {
      WButton: Button,
      WidgetIcon,
      GoogleMap,
      Modal,
      ControlWrapper,
    },
    props: {
      ...rendererProps<ControlElement>(),
    },
    setup(props: RendererProps<ControlElement>) {
      const isMapOpen = ref(false);
      const isOpenDeleteModal = ref(false);
      const control = useWidgetControl(useJsonFormsControl(props));
      const controlOptions = ref(
        (control.control.value.schema as unknown as { locationOptions: LocationOptions }).locationOptions
      );
      const currentLocation = ref<ILocationInfo | undefined>(control.control.value.data || undefined);
      function toggleMap(value: boolean) {
        isMapOpen.value = value;
        control.setIsFocused(!value);
      }

      function locationChange(location: ILocationInfo | undefined) {
        currentLocation.value = location;
        control.onChange(location);
      }

      function deleteLocation() {
        currentLocation.value = undefined;
        control.onChange(undefined);
        isOpenDeleteModal.value = false;
      }
      return {
        currentLocation,
        locationChange,
        isMapOpen,
        isOpenDeleteModal,
        config,
        toggleMap,
        deleteLocation,
        ...control,
        themeIcons,
        controlOptions,
      };
    },
  }) as unknown as typeof defineComponent;
</script>
